import {useQuery} from "@tanstack/react-query";
import {fetchSentimentInfluencers, type SentimentInfluencerWithPosts} from "@/lib/influencers-api";
import {isPositive, isNegative} from "@/lib/sentiment-utils";

// ============================================================================
// Query Key
// ============================================================================

export const SENTIMENT_INFLUENCERS_KEY = "sentiment-influencers";

// ============================================================================
// Query Hook
// ============================================================================

/**
 * Fetch sentiment influencers for a location
 * @param locationId - Location ID to fetch influencers for (null disables the query)
 */
export function useSentimentInfluencers(locationId: string | null) {
  return useQuery<SentimentInfluencerWithPosts[], Error>({
    queryKey: [SENTIMENT_INFLUENCERS_KEY, locationId],
    queryFn: () => fetchSentimentInfluencers(locationId ?? ""),
    enabled: Boolean(locationId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

// ============================================================================
// Filter Utilities
// ============================================================================

export type SentimentType = "positive" | "negative";

/**
 * Filter influencers by sentiment type and sort by influence score
 * @param influencers - Array of influencers to filter
 * @param sentiment - Sentiment type to filter by
 * @returns Filtered and sorted array of influencers
 */
export function filterInfluencersBySentiment(
  influencers: SentimentInfluencerWithPosts[],
  sentiment: SentimentType
): SentimentInfluencerWithPosts[] {
  return influencers
    .filter((account) =>
      sentiment === "positive" ? isPositive(account.sentiment) : isNegative(account.sentiment)
    )
    .sort((a, b) => b.combined_influence_score - a.combined_influence_score);
}

/**
 * Count influencers by sentiment type
 * @param influencers - Array of influencers to count
 * @returns Object with positive and negative counts
 */
export function countInfluencersBySentiment(influencers: SentimentInfluencerWithPosts[]): {
  positive: number;
  negative: number;
} {
  let positive = 0;
  let negative = 0;

  for (const account of influencers) {
    if (isPositive(account.sentiment)) {
      positive++;
    } else if (isNegative(account.sentiment)) {
      negative++;
    }
  }

  return {positive, negative};
}
